// src/components/Admin/tabs/ProductsTab.jsx
// Productos add-on que el cliente puede agregar a su reservación.
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  createProductAdmin,
  updateProductAdmin,
  deleteProductAdmin,
} from '../../../redux/slices/Admin/adminSlice';
import { money } from '../adminUtils';
import AdminImagesField from '../AdminImagesField';

// Debe coincidir con PRODUCT_CATEGORIES en macarios-api/model/Product.js
const CATEGORIES = [
  { value: 'snacks', label: 'Snacks' },
  { value: 'juguetes', label: 'Juguetes' },
  { value: 'higiene', label: 'Higiene' },
  { value: 'accesorios', label: 'Accesorios' },
  { value: 'servicios', label: 'Servicios' },
  { value: 'otros', label: 'Otros' },
];

// Debe coincidir con MAX_PRODUCT_IMAGES en macarios-api/model/Product.js
const MAX_IMAGES = 3;

const EMPTY_FORM = {
  name: '',
  description: '',
  nameEn: '',
  descriptionEn: '',
  category: 'otros',
  price: '',
  totalQty: '',
  isActive: true,
};

const inputClass =
  'w-full p-2.5 border border-[#3A2A20]/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 focus:border-[#9A0002] transition text-sm';
const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60 mb-1.5';

const ProductsTab = ({ products }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY_FORM);
  const [keptImages, setKeptImages] = useState([]); // URLs actuales que se conservan
  const [newImages, setNewImages] = useState([]); // File[] por subir
  const [editingId, setEditingId] = useState(null); // null = creando
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setKeptImages([]);
    setNewImages([]);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEdit = (product) => {
    setForm({
      name: product.name,
      description: product.description || '',
      nameEn: product.nameEn || '',
      descriptionEn: product.descriptionEn || '',
      category: product.category || 'otros',
      price: String(product.price),
      totalQty: product.totalQty === null || product.totalQty === undefined ? '' : String(product.totalQty),
      isActive: product.isActive,
    });
    setKeptImages(product.images || []);
    setNewImages([]);
    setEditingId(product._id);
    setShowForm(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // FormData porque puede incluir imágenes
    const productData = new FormData();
    Object.entries(form).forEach(([key, value]) => productData.append(key, String(value)));
    if (editingId) productData.append('keepImages', JSON.stringify(keptImages));
    newImages.forEach((file) => productData.append('images', file));

    const result = editingId
      ? await dispatch(updateProductAdmin({ id: editingId, productData }))
      : await dispatch(createProductAdmin(productData));

    setSaving(false);
    if (result.error) {
      setError(result.payload || 'No se pudo guardar el producto');
      return;
    }
    resetForm();
  };

  const handleToggleActive = (product) => {
    const productData = new FormData();
    productData.append('isActive', String(!product.isActive));
    dispatch(updateProductAdmin({ id: product._id, productData }));
  };

  const handleDelete = (product) => {
    if (window.confirm(`¿Eliminar "${product.name}"? Las reservas anteriores lo conservan.`)) {
      dispatch(deleteProductAdmin(product._id));
    }
  };

  const stockLabel = (p) => {
    if (p.totalQty === null || p.totalQty === undefined) return 'Sin límite';
    return `${p.qtyLeft ?? p.totalQty - p.totalSold} de ${p.totalQty} disponibles`;
  };

  return (
    <div>
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2.5 text-sm font-medium bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl mb-4 transition"
        >
          + Agregar producto
        </button>
      )}

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-white border border-[#3A2A20]/10 rounded-2xl p-6 mb-6 space-y-4"
        >
          <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20]">
            {editingId ? 'Editar producto' : 'Nuevo producto'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Categoría</label>
              <select name="category" value={form.category} onChange={handleChange} className={inputClass}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Precio (MXN)</label>
              <input
                name="price"
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Inventario</label>
              <input
                name="totalQty"
                type="number"
                min="0"
                step="1"
                value={form.totalQty}
                onChange={handleChange}
                placeholder="Vacío = sin límite (ej. servicios)"
                className={inputClass}
              />
            </div>
          </div>

          {/* Textos en los dos idiomas de la tienda */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#3A2A20]/10 bg-[#EFE6DD]/40 p-4 space-y-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#3A2A20]">
                <span className="fi fi-mx fis rounded-sm" /> Español
              </p>
              <div>
                <label className={labelClass}>Nombre</label>
                <input name="name" value={form.name} onChange={handleChange} required maxLength={100} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Descripción</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows="3"
                  maxLength={500}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="rounded-xl border border-[#3A2A20]/10 bg-[#EFE6DD]/40 p-4 space-y-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#3A2A20]">
                <span className="fi fi-us fis rounded-sm" /> English
                <span className="text-xs font-normal text-[#3A2A20]/50">· opcional, si está vacío se muestra en español</span>
              </p>
              <div>
                <label className={labelClass}>Name</label>
                <input
                  name="nameEn"
                  value={form.nameEn}
                  onChange={handleChange}
                  maxLength={100}
                  placeholder={form.name}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  name="descriptionEn"
                  value={form.descriptionEn}
                  onChange={handleChange}
                  rows="3"
                  maxLength={500}
                  placeholder={form.description}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          <AdminImagesField
            keptImages={keptImages}
            setKeptImages={setKeptImages}
            newImages={newImages}
            setNewImages={setNewImages}
            max={MAX_IMAGES}
            onError={setError}
            labelClass={labelClass}
          />

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-[#3A2A20]">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                className="w-4 h-4 accent-[#9A0002]"
              />
              Visible para clientes
            </label>
          </div>

          {error && <p className="text-[#9A0002] text-sm">{error}</p>}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 text-sm font-medium bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl transition disabled:opacity-50"
            >
              {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear producto'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-5 py-2.5 text-sm font-medium border border-[#3A2A20]/20 text-[#3A2A20] rounded-xl hover:bg-[#3A2A20]/5 transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {products.length === 0 ? (
        <p className="text-sm text-[#3A2A20]/60">
          Aún no hay productos. Los que agregues aparecerán en el formulario de reservación.
        </p>
      ) : (
        <div className="grid gap-3">
          {products.map((p) => (
            <div
              key={p._id}
              className={`bg-white border border-[#3A2A20]/10 rounded-xl p-4 flex flex-wrap gap-4 justify-between items-center ${
                p.isActive ? '' : 'opacity-60'
              }`}
            >
              <div className="flex items-center gap-4 min-w-0">
                {p.images?.[0] ? (
                  <div className="relative shrink-0">
                    <img src={p.images[0]} alt={p.name} className="w-14 h-14 rounded-lg object-cover" />
                    {p.images.length > 1 && (
                      <span className="absolute -bottom-1 -right-1 px-1.5 rounded-full bg-[#3A2A20] text-white text-[10px] font-semibold">
                        {p.images.length}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-[#EFE6DD] shrink-0" />
                )}
                <div className="min-w-0">
                  <h3 className="font-semibold text-[#3A2A20] truncate">
                    {p.name}
                    {!p.isActive && <span className="ml-2 text-xs font-normal text-[#3A2A20]/50">(oculto)</span>}
                    {!p.nameEn && (
                      <span className="ml-2 text-xs font-normal text-[#9A0002]/70">(sin inglés)</span>
                    )}
                  </h3>
                  <p className="text-sm text-[#3A2A20]/60">
                    {money(p.price)} · {CATEGORIES.find((c) => c.value === p.category)?.label || p.category} · {stockLabel(p)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleActive(p)}
                  className="px-3 py-1.5 text-xs font-medium bg-[#3A2A20]/5 text-[#3A2A20] rounded-lg hover:bg-[#3A2A20]/10 transition"
                >
                  {p.isActive ? 'Ocultar' : 'Mostrar'}
                </button>
                <button
                  onClick={() => handleEdit(p)}
                  className="px-3 py-1.5 text-xs font-medium bg-[#3A2A20]/5 text-[#3A2A20] rounded-lg hover:bg-[#3A2A20]/10 transition"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(p)}
                  className="px-3 py-1.5 text-xs font-medium bg-[#9A0002]/10 text-[#9A0002] rounded-lg hover:bg-[#9A0002]/20 transition"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsTab;
